import AuthLayout from '@layouts/AuthLayout';
import ProtectedLayout from '@layouts/ProtectedLayout';
import MainAppLayout from '@layouts/MainAppLayout';
import { lazyImportWithRetry } from '@utils/lazyImportWithRetry';
import React, { Suspense, type ComponentType } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router';
import Support from '@pages/Support';

const lazyRoute = <TModule extends { default: ComponentType<any> }>(
  importer: () => Promise<TModule>,
) => React.lazy(() => lazyImportWithRetry(importer));

const Home = lazyRoute(() => import('@pages/Home'));
const MasqueradePage = lazyRoute(() => import('@pages/Masquerade'));
const AuthCallbackPage = lazyRoute(() => import('@pages/Auth/AuthCallback'));
const AuthCallbackCognitoPage = lazyRoute(
  () => import('@pages/Auth/AuthCallbackCognito'),
);
const SignupPage = lazyRoute(() => import('@pages/Auth/Signup'));
const ForgotPasswordPage = lazyRoute(() => import('@pages/Auth/ForgotPassword'));
const LoginPage = lazyRoute(() => import('@pages/Auth/Login'));
const ResetPasswordPage = lazyRoute(() => import('@pages/Auth/ResetPassword'));
const ResetTempPasswordPage = lazyRoute(
  () => import('@pages/Auth/ResetTempPassword'),
);
const OnboardingPage = lazyRoute(() => import('@pages/Onboarding'));
const SettingsPage = lazyRoute(() => import('@pages/Settings'));

// Dev Pages - uncomment when needed
// const NotificationTestPage = React.lazy(() => import('@pages/dev/NotificationTestPage'));

const AppRoutes = () => {
  return (
    <Router>
      <Suspense>
        <Routes>
          <Route path="callback" element={<AuthCallbackCognitoPage />} />
          <Route element={<AuthLayout />} path="auth">
            <Route path="login" element={<LoginPage />} />
            <Route path="signup" element={<SignupPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route
              path="reset-temp-password"
              element={<ResetTempPasswordPage />}
            />
          </Route>
          <Route element={<ProtectedLayout />}>
            {/* Routes that bypass MainAppLayout */}
            <Route path="/auth-callback" element={<AuthCallbackPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />

            {/* Routes that use MainAppLayout */}
            <Route element={<MainAppLayout />}>
              <Route path="/" element={<Home />} >
                <Route path="support" element={<Support />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="masquerade" element={<MasqueradePage />} />
              </Route>
              {/* Dev routes - uncomment when needed */}
              {/* <Route path="/dev/notifications" element={<NotificationTestPage />} /> */}
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
